import { parseActionGraph } from "./actions.ts";
import {
	AttackSchema,
	CurrencySchema,
	ItemSchema,
	PlaceSchema,
	QuestSchema,
	RecipeSchema,
	StatSchema,
	type ProjectView,
} from "./entities.ts";
import {
	formatPath,
	graphContainsBehavior,
	graphContainsType,
	issue,
	newReport,
	pushBrokenRef,
	validateShape,
} from "./validate/helpers.ts";
import type {
	ProjectConfig,
	ValidationReport,
} from "./validate/types.ts";
import { computeUnused } from "./validate/unused.ts";
import { walkGraph, walkUiNode } from "./validate/walk.ts";

export type {
	Issue,
	IssueCode,
	IssueContext,
	IssuePath,
	ProjectConfig,
	ValidationReport,
} from "./validate/types.ts";

export const validateProject = (
	view: ProjectView,
	config?: ProjectConfig,
): ValidationReport => {
	const report = newReport();
	validateItems(view, report);
	validateAttacks(view, report);
	validatePlayerItems(view, report);
	validateConfig(config, report);
	validateRecipes(view, report);
	validateCurrencies(view, report);
	validateStats(view, report);
	validateQuests(view, report);
	validatePlaces(view, report);
	validateUiTrees(view, report);
	computeUnused(view, report);
	return report;
};

const validateItems = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	for (const [id, item] of Object.entries(view.items)) {
		validateShape(ItemSchema, item, "items", id, report);
		if (item.attacks) {
			item.attacks.forEach((aid, i) => {
				if (!view.attacks[aid]) {
					report.errors.push(
						issue(
							"error",
							"items",
							id,
							["attacks", i],
							`References unknown attack '${aid}'`,
							"broken_ref",
						),
					);
					pushBrokenRef(report, "items", id, formatPath(["attacks", i]));
				}
			});
		}

		if (typeof item.matchQuest === "string" && item.matchQuest) {
			const linked = view.quests[item.matchQuest];
			if (!linked) {
				report.errors.push(
					issue(
						"error",
						"items",
						id,
						["matchQuest"],
						`References unknown quest '${item.matchQuest}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(report, "items", id, formatPath(["matchQuest"]));
			} else if (linked.type !== "Match") {
				report.errors.push(
					issue(
						"error",
						"items",
						id,
						["matchQuest"],
						`matchQuest must reference a quest of type 'Match' (got '${linked.type}')`,
						"invalid_value",
					),
				);
			}
		}

		const graph = parseActionGraph(item.graph);
		walkGraph(graph, view, id, item, report);

		const hasClaim =
			graphContainsType(graph, "ClaimQuest") ||
			graphContainsType(graph, "CheckoutQuest");
		const hasGive = graphContainsType(graph, "GiveQuest");
		if (hasClaim && !hasGive) {
			report.errors.push(
				issue(
					"error",
					"items",
					id,
					["graph"],
					`Item '${id}' has a ClaimQuest/CheckoutQuest node but no GiveQuest — the giver must also be the claimer`,
					"claim_without_give",
				),
			);
		}

const usesAggressive = graphContainsBehavior(graph, ["Aggressive"]);
		if (usesAggressive) {
			const hasStats = Object.keys(item.stats ?? {}).length > 0;
			const hasAttacks =
				Array.isArray(item.attacks) && item.attacks.length > 0;
			if (!hasStats) {
				report.warnings.push(
					issue(
						"warning",
						"items",
						id,
						["stats"],
						`Item '${id}' uses ChangeBehavior Aggressive but has no stats — runtime applies graceful fallback stats`,
						"missing_field",
					),
				);
			}
			if (!hasAttacks) {
				report.warnings.push(
					issue(
						"warning",
						"items",
						id,
						["attacks"],
						`Item '${id}' uses ChangeBehavior Aggressive but has no attacks — entity will be passive`,
						"missing_field",
					),
				);
			}
		}
	}
};

const validateAttacks = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	for (const [id, attack] of Object.entries(view.attacks)) {
		validateShape(AttackSchema, attack, "attacks", id, report);
		if (attack.costStat && !view.stats[attack.costStat]) {
			report.errors.push(
				issue(
					"error",
					"attacks",
					id,
					["costStat"],
					`References unknown stat '${attack.costStat}'`,
					"broken_ref",
				),
			);
			pushBrokenRef(report, "attacks", id, "costStat");
		}
		if (attack.damage) {
			attack.damage.forEach((d, i) => {
				if (d.statKey && !view.stats[d.statKey]) {
					report.errors.push(
						issue(
							"error",
							"attacks",
							id,
							["damage", i, "statKey"],
							`References unknown stat '${d.statKey}'`,
							"broken_ref",
						),
					);
					pushBrokenRef(
						report,
						"attacks",
						id,
						formatPath(["damage", i, "statKey"]),
					);
				}
			});
		}
	}
};

const validatePlayerItems = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	const playerItems = Object.values(view.items).filter(
		(item) => item.category === "Player",
	);
	const itemCount = Object.keys(view.items).length;
	if (itemCount > 0 && playerItems.length === 0) {
		report.errors.push(
			issue(
				"error",
				"global",
				null,
				["items"],
				"No item with category 'Player' — exactly one Player item must describe the character (starting stats, attacks, lifecycle events)",
				"no_player_item",
			),
		);
	}
	if (playerItems.length > 1) {
		report.errors.push(
			issue(
				"error",
				"global",
				null,
				["items"],
				`Multiple items with category 'Player' (${playerItems.map((i) => i.id).join(", ")}) — exactly one is allowed`,
				"multiple_player_items",
			),
		);
	}
};

const validateConfig = (
	config: ProjectConfig | undefined,
	report: ValidationReport,
): void => {
	if (config) {
		const defaultZone = config.DefaultWorldZone;
		if (typeof defaultZone !== "string" || defaultZone.trim() === "") {
			report.errors.push(
				issue(
					"error",
					"global",
					null,
					["config", "DefaultWorldZone"],
					"DefaultWorldZone is empty — players will spawn at the Roblox default origin (0,0,0). Set it to a public zone name in Configuration → World.",
					"missing_default_world_zone",
				),
			);
		}
	}
};

const validateRecipes = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	const recipesByDeveloperProductId = new Map<string, string>();
	for (const [id, recipe] of Object.entries(view.recipes)) {
		validateShape(RecipeSchema, recipe, "recipes", id, report);
		const developerProductId =
			typeof recipe.developerProductId === "string"
				? recipe.developerProductId.trim()
				: "";
		if (developerProductId.length > 0) {
			const existingRecipeId = recipesByDeveloperProductId.get(developerProductId);
			if (existingRecipeId !== undefined) {
				report.errors.push(
					issue(
						"error",
						"recipes",
						id,
						["developerProductId"],
						`developerProductId '${developerProductId}' is already used by recipe '${existingRecipeId}'`,
						"duplicate_developer_product_id",
					),
				);
			} else {
				recipesByDeveloperProductId.set(developerProductId, id);
			}
		}
		recipe.inputs?.forEach((input, i) => {
			if (input.type === "item" && !view.items[input.itemId]) {
				report.errors.push(
					issue(
						"error",
						"recipes",
						id,
						["inputs", i, "itemId"],
						`References unknown item '${input.itemId}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"recipes",
					id,
					formatPath(["inputs", i, "itemId"]),
				);
			}
			if (input.type === "currency" && !view.currencies[input.currencyId]) {
				report.errors.push(
					issue(
						"error",
						"recipes",
						id,
						["inputs", i, "currencyId"],
						`References unknown currency '${input.currencyId}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"recipes",
					id,
					formatPath(["inputs", i, "currencyId"]),
				);
			}
		});
		recipe.outputs?.forEach((output, i) => {
			if (output.type === "item" && !view.items[output.itemId]) {
				report.errors.push(
					issue(
						"error",
						"recipes",
						id,
						["outputs", i, "itemId"],
						`References unknown item '${output.itemId}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"recipes",
					id,
					formatPath(["outputs", i, "itemId"]),
				);
			}
			if (output.type === "currency" && !view.currencies[output.currencyId]) {
				report.errors.push(
					issue(
						"error",
						"recipes",
						id,
						["outputs", i, "currencyId"],
						`References unknown currency '${output.currencyId}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"recipes",
					id,
					formatPath(["outputs", i, "currencyId"]),
				);
			}
		});
	}
};

const validateCurrencies = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	for (const [id, currency] of Object.entries(view.currencies)) {
		validateShape(CurrencySchema, currency, "currencies", id, report);
	}
};

const validateStats = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	for (const [id, stat] of Object.entries(view.stats)) {
		validateShape(StatSchema, stat, "stats", id, report);
	}
};

const validateQuests = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	const validateObjectiveRefs = (
		id: string,
		listName: "winObjectives" | "loseObjectives",
		objectives: unknown,
	): void => {
		if (!Array.isArray(objectives)) return;
		objectives.forEach((objective, oi) => {
			if (!objective || typeof objective !== "object") return;
			const raw = objective as { action?: unknown; itemId?: unknown };
			const itemId = raw.itemId;
			if (typeof itemId !== "string" || !itemId) return;
			const path = [listName, oi, "itemId"];
			if (raw.action === "Craft") {
				if (!view.recipes[itemId]) {
					report.errors.push(
						issue(
							"error",
							"quests",
							id,
							path,
							`References unknown recipe '${itemId}'`,
							"broken_ref",
						),
					);
					pushBrokenRef(report, "quests", id, formatPath(path));
				}
			} else if (raw.action === "ReachStat") {
				if (!view.stats[itemId]) {
					report.errors.push(
						issue(
							"error",
							"quests",
							id,
							path,
							`References unknown stat '${itemId}'`,
							"broken_ref",
						),
					);
					pushBrokenRef(report, "quests", id, formatPath(path));
				}
			} else if (
				raw.action === "Kill" ||
				raw.action === "Consume" ||
				raw.action === "Collect" ||
				raw.action === "Die"
			) {
				if (!view.items[itemId]) {
					report.errors.push(
						issue(
							"error",
							"quests",
							id,
							path,
							`References unknown item '${itemId}'`,
							"broken_ref",
						),
					);
					pushBrokenRef(report, "quests", id, formatPath(path));
				}
			}
		});
	};

	for (const [id, quest] of Object.entries(view.quests)) {
		validateShape(QuestSchema, quest, "quests", id, report);
		validateObjectiveRefs(id, "winObjectives", quest.winObjectives);
		validateObjectiveRefs(id, "loseObjectives", quest.loseObjectives);
		quest.rewards.forEach((reward, i) => {
			if (reward.type === "item" && !view.items[reward.itemId]) {
				report.errors.push(
					issue(
						"error",
						"quests",
						id,
						["rewards", i, "itemId"],
						`References unknown item '${reward.itemId}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"quests",
					id,
					formatPath(["rewards", i, "itemId"]),
				);
			}
			if (reward.type === "currency" && !view.currencies[reward.currencyId]) {
				report.errors.push(
					issue(
						"error",
						"quests",
						id,
						["rewards", i, "currencyId"],
						`References unknown currency '${reward.currencyId}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"quests",
					id,
					formatPath(["rewards", i, "currencyId"]),
				);
			}
			if (reward.type === "stat" && !view.stats[reward.statKey]) {
				report.errors.push(
					issue(
						"error",
						"quests",
						id,
						["rewards", i, "statKey"],
						`References unknown stat '${reward.statKey}'`,
						"broken_ref",
					),
				);
				pushBrokenRef(
					report,
					"quests",
					id,
					formatPath(["rewards", i, "statKey"]),
				);
			}
		});
	}
};

const validatePlaces = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	const places = view.places ?? {};
	const seenPaths = new Map<string, string>();
	for (const [id, place] of Object.entries(places)) {
		validateShape(PlaceSchema, place, "global", id, report);
		const existing = seenPaths.get(place.path);
		if (existing && existing !== id) {
			report.errors.push(
				issue(
					"error",
					"global",
					id,
					["places", id, "path"],
					`Duplicate place path '${place.path}' (also used by '${existing}') — each Roblox folder can only be declared once`,
					"duplicate_place_path",
				),
			);
		} else {
			seenPaths.set(place.path, id);
		}
	}
};

const validateUiTrees = (
	view: ProjectView,
	report: ValidationReport,
): void => {
	const uiTrees = view.uiTrees ?? {};
	const treesByRole = new Map<string, string[]>();
	for (const [treeId, tree] of Object.entries(uiTrees)) {
		walkUiNode(tree.root, view, treeId, report);
		const { role } = tree;
		if (typeof role === "string" && role !== "") {
			const list = treesByRole.get(role) ?? [];
			list.push(treeId);
			treesByRole.set(role, list);
		}
	}

	for (const [role, treeIds] of treesByRole) {
		if (treeIds.length > 1) {
			report.warnings.push(
				issue(
					"warning",
					"global",
					null,
					["uiTrees"],
					`Plusieurs UiTrees déclarent role='${role}' (${treeIds.join(", ")}) — le runtime prendra la première et ignorera les autres jusqu'au selector`,
					"duplicate_uitree_role",
				),
			);
		}
	}
};
